import React, { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import LocationList, { LocationType } from '../LocationList';
import { 
  importLocationsFromFile,
  SETUP_SUBMODES,
  isSetupMode
} from '../../Services';
import '../styles/modes/setupMode.css';

// Import sub-components
import ModeHelpTooltip from './ModeHelpTooltip';
import ManualInputForm from './ManualInputForm';
import SubmodeSelector from './SubmodeSelector';
import SetupActions from './SetupActions';

export default function SetupMode(props) {
  const { 
    locations = [], 
    startId = '', 
    endId = '',
    setupSubmode, 
    onSubmodeSwitching, 
    setLocations, 
    setStartId,
    setEndId,
    setPath,          // Function to set path
    currentModeRef    // Reference to current mode
  } = props;
  
  // Manual input form data
  const [newLocation, setNewLocation] = useState({
    latitude: '',
    longitude: ''
  });
  // Error message
  const [error, setError] = useState('');
  // Import error message
  const [importError, setImportError] = useState('');
  // File input reference
  const fileInputRef = useRef(null);
  // Help tooltip state
  const [showHelp, setShowHelp] = useState(false);

  // Toggle help visibility
  const toggleHelp = () => setShowHelp(!showHelp);

  // Switch submode
  const switchSubmode = (mode) => {
    onSubmodeSwitching(mode);
  };
  
  // Manually add location point
  const handleAddManualLocation = (e) => {
    e.preventDefault();
    
    const { latitude, longitude } = newLocation;
    
    // Validate input
    if (!latitude || !longitude) {
      setError("Please fill in complete location information");
      return;
    }
    
    // Convert to numbers
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    
    // Validate coordinates
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError("Please enter valid coordinates");
      return;
    }
    
    // Create new location point
    const id = uuidv4();
    const pointName = `${locations.length + 1}`;
    const newPoint = {
      id,
      name: pointName,
      latitude: lat,
      longitude: lng,
      type: LocationType.NORMAL
    };
    
    // Update location list
    setLocations([...locations, newPoint]);
    setError('');
    
    // Reset form
    setNewLocation({ latitude: '', longitude: '' });
  };
  
  // Form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLocation({
      ...newLocation,
      [name]: value
    });
  };

  // Handle file import click
  const handleImportClick = () => {
    // Clear previous errors
    setImportError('');
    // Trigger file selection dialog
    fileInputRef.current.click();
  };

  // Handle file change/upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Import location points using universal importer
      const importedLocations = await importLocationsFromFile(file);
      
      // Renumber location points while preserving original types
      const renumberedLocations = importedLocations.map((loc, index) => ({
        ...loc,
        name: `${index + 1}`
      }));
      
      // Update location list
      setLocations(renumberedLocations);
      
      // Check if there's a location point marked as start point
      const startPoint = renumberedLocations.find(loc => loc.type === LocationType.START);
      if (startPoint) {
        // If a start point is found, set its ID as the start point
        setStartId(startPoint.id);
      } else {
        // If no start point is found, don't automatically set a start point
        setStartId('');
      }
      
      // Reset file input to allow re-uploading the same file
      e.target.value = '';
    } catch (error) {
      console.error('Failed to import location points:', error);
      setImportError(error.message || 'Failed to import location points');
      // Reset file input
      e.target.value = '';
    }
  };

  // Reset all data (location points and paths)
  const handleReset = () => {
    // Clear location points
    setLocations([]);
    // Clear start point
    setStartId('');
    
    // Clear all paths - regardless of current mode, clear both auto and custom paths
    if (setPath) {

      // Clear paths for current mode
      setPath([]);
      
      // Send empty vehicle path info to ensure custom path is cleared
      setPath([]);
      
      // Send empty auto path info to ensure auto path is cleared
      setPath([]);
    }
    
    // Clear errors
    setError('');
    setImportError('');
  };

  // Use passed setupSubmode instead of local state
  const displaySubmode = setupSubmode || SETUP_SUBMODES.MAP;

  return (
    <>
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Set Path Points</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Set Path Points mode"
          >
            ?
          </button>
        </div>
        <p>Please add the location points to visit and set the starting point.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      {/* Submode selector */}
      <SubmodeSelector 
        displaySubmode={displaySubmode}
        switchSubmode={switchSubmode}
        handleImportClick={handleImportClick}
        importError={importError}
        fileInputRef={fileInputRef}
        handleFileChange={handleFileChange}
        submodes={SETUP_SUBMODES}
      />
      
      {/* Manual input form - only shown in manual input mode */}
      {displaySubmode === SETUP_SUBMODES.MANUAL && (
        <ManualInputForm 
          newLocation={newLocation}
          error={error}
          handleInputChange={handleInputChange}
          handleAddManualLocation={handleAddManualLocation}
        />
      )}
      
      {/* Use LocationList component to manage path points */}
      <LocationList 
        locations={locations}
        startId={startId}
        endId={endId}
        mode="Set Path Points"
        readOnly={false}
        showActions={true}
        allowDelete={true}
        allowSetStart={true}
        allowSetEnd={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        onEndIdChange={setEndId}
      />
      
      {/* Setup actions */}
      <SetupActions 
        locations={locations}
        handleReset={handleReset}
      />
    </>
  );
} 